import { ApiService } from "../../../config";

const advisorRequestConfig = {
  headers: {
    "x-modal-tab": "Assistente IA",
  },
};

const getAdvisorErrorMessage = (error: unknown, fallback: string) => {
  const responseMessage = (error as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  const errorMessage = error instanceof Error ? error.message : undefined;
  return responseMessage || errorMessage || fallback;
};

const ensureAdvisorSuccess = (response: unknown, fallback: string) => {
  const data = (response as { data?: { error?: boolean; message?: string } })
    ?.data;

  if (data?.error) {
    throw new Error(data.message || fallback);
  }
};

export interface CategorySuggestionRequest {
  displayName: string;
  formattedAddress: string;
  types?: string[];
}

export interface KeywordSuggestionRequest {
  displayName: string;
  formattedAddress: string;
  categories?: string[];
  currentKeywords?: string[];
}

export interface BriefingSuggestionRequest {
  displayName: string;
  formattedAddress: string;
  categories?: (string | { gcid: string; category: string })[];
  keywords?: string[];
}

export interface DescriptionSuggestionRequest {
  displayName: string;
  formattedAddress: string;
  primaryCategory?: string;
  categories?: string[];
  keywords?: string[];
  /** O que o operador escreveu em "o que quer vender ou destacar". */
  context?: string;
}

export const profileAdvisorService = {
  /**
   * Obtém sugestões de categorias de negócio com base no nome e endereço.
   * Retorna uma sugestão primária e sugestões adicionais.
   */
  async getSuggestedCategories(request: CategorySuggestionRequest) {
    try {
      const response = await ApiService.post(
        "/business-profile-advisor/recommended-categories",
        request,
        advisorRequestConfig,
      );

      ensureAdvisorSuccess(response, "Erro ao sugerir categorias.");

      const categoriesData = response?.data?.data;
      if (categoriesData) {
        const suggestions = categoriesData.additionalSuggestion || [];
        if (categoriesData.primarySuggestion) {
          return [categoriesData.primarySuggestion, ...suggestions];
        }
        return suggestions;
      }
      return [];
    } catch (error) {
      throw new Error(
        getAdvisorErrorMessage(error, "Erro ao sugerir categorias."),
      );
    }
  },

  /**
   * Obtém sugestões de palavras-chave para o perfil de negócio.
   * Baseado no nome, endereço e categorias do negócio.
   */
  async getSuggestedKeywords(request: KeywordSuggestionRequest) {
    try {
      const response = await ApiService.post(
        "/business-profile-advisor/recommended-keywords",
        request,
        advisorRequestConfig,
      );

      ensureAdvisorSuccess(response, "Erro ao sugerir palavras-chave.");

      const keywordsData = response?.data?.data;
      return keywordsData?.keywords || [];
    } catch (error) {
      throw new Error(
        getAdvisorErrorMessage(error, "Erro ao sugerir palavras-chave."),
      );
    }
  },

  /**
   * Gera sugestão de briefing completa para o perfil.
   * Inclui análise de categoria, palavras-chave e descrição.
   */
  async getSuggestedBriefing(request: BriefingSuggestionRequest) {
    try {
      const response = await ApiService.post(
        "/business-profile-advisor/recommended-briefing",
        request,
        advisorRequestConfig,
      );

      ensureAdvisorSuccess(response, "Erro ao gerar descrição.");

      const briefingData = response?.data?.data;
      return briefingData || "";
    } catch (error) {
      throw new Error(getAdvisorErrorMessage(error, "Erro ao gerar descrição."));
    }
  },

  /**
   * Gera a descrição pública da ficha (até 750 caracteres) pela IA.
   * Quem chama decide o que fazer se vier erro ou texto vazio.
   */
  async getSuggestedDescription(
    request: DescriptionSuggestionRequest,
  ): Promise<string> {
    try {
      const response = await ApiService.post(
        "/business-profile-advisor/recommended-description",
        request,
        advisorRequestConfig,
      );

      ensureAdvisorSuccess(response, "Erro ao gerar descrição.");

      const description = response?.data?.data;
      return typeof description === "string" ? description : "";
    } catch (error) {
      throw new Error(getAdvisorErrorMessage(error, "Erro ao gerar descrição."));
    }
  },

  /**
   * Altera o status de rastreamento do perfil de negócio.
   * Ativa ou desativa o monitoramento de palavras-chave e métricas.
   */
  async changeTrackingStatus(businessProfileId: string, tracking: boolean) {
    try {
      const response = await ApiService.post(
        `/business-profiles/${businessProfileId}/changeTracking`,
        { tracking },
      );
      return response?.data?.data || "";
    } catch {
      return "";
    }
  },
};
